<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">BDM Birthdays Today</h4>
        <div class="text-muted">
          BDM-managed users with birthdays today (Australia/Sydney timezone) for
          birthday greetings
        </div>
      </div>

      <div>
        <b-button variant="primary" @click="handleRefresh">
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
          Refresh
        </b-button>
      </div>
    </div>

    <!-- STATS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Birthdays Today</div>
              <div class="h5 font-weight-bold">
                {{ bdmBirthdaysToday.length }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Last Updated</div>
              <div class="small text-muted">{{ lastUpdated }}</div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- MESSAGE -->
    <b-alert
      v-if="bdmBirthdaysToday.length === 0"
      variant="info"
      show
      class="mb-3"
    >
      <feather-icon icon="CheckCircleIcon" size="16" class="mr-2" />
      No birthdays today. Check back tomorrow!
    </b-alert>

    <!-- TABLE/CARDS -->
    <b-card no-body>
      <div class="m-2">
        <label
          >Grouped by BDM Segment ({{ bdmBirthdaysToday.length }} users)</label
        >
      </div>

      <div v-if="bdmBirthdaysToday.length > 0">
        <div v-for="bdm in groupedByBdm" :key="bdm.tag" class="bdm-section">
          <!-- BDM TAG HEADER -->
          <div class="bdm-header">
            <b-badge variant="info" class="bdm-badge">
              {{ bdm.tag }}
            </b-badge>
            <span class="ml-2 text-muted"
              >({{ bdm.users.length }} birthdays)</span
            >
          </div>

          <!-- USERS GRID -->
          <b-row class="bdm-users">
            <b-col
              v-for="user in bdm.users"
              :key="user.userId"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mb-3"
            >
              <b-card class="user-card h-100">
                <div class="d-flex align-items-center mb-2">
                  <div class="birthday-icon mr-3" style="font-size: 2rem">
                    🎂
                  </div>
                  <div>
                    <div class="font-weight-bold">
                      {{ user.firstName }} {{ user.lastName }}
                    </div>
                    <div class="small text-muted">{{ user.userId }}</div>
                  </div>
                </div>

                <hr class="my-2" />

                <div class="small mb-2">
                  <div class="text-muted">Email</div>
                  <div class="text-break">{{ user.email }}</div>
                </div>

                <div class="small">
                  <div class="text-muted">DOB</div>
                  <div class="font-weight-semibold">
                    {{ formatDate(user.dob) }}
                  </div>
                </div>

                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mt-3 w-100"
                  @click="sendGreeting(user)"
                >
                  <feather-icon icon="SendIcon" size="14" class="mr-1" />
                  Send Greeting
                </b-button>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </div>

      <div v-else class="text-center text-muted py-4">
        <p>No birthdays to celebrate today</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge, BAlert } from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { format } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BAlert,
  },
  data() {
    return {
      lastUpdated: new Date().toLocaleTimeString(),
    };
  },
  computed: {
    ...mapState("reporting", ["bdmBirthdaysToday", "loadingBdmBirthdaysToday"]),
    groupedByBdm() {
      const grouped = {};
      (this.bdmBirthdaysToday || []).forEach((user) => {
        const tag = user.bdmTag || "Unassigned";
        if (!grouped[tag]) {
          grouped[tag] = { tag, users: [] };
        }
        grouped[tag].users.push(user);
      });

      return Object.values(grouped).sort((a, b) => a.tag.localeCompare(b.tag));
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("reporting", ["getBdmBirthdaysToday"]),
    formatCurrencyBuiltInFunc,
    async fetchData() {
      await this.getBdmBirthdaysToday();
      this.lastUpdated = new Date().toLocaleTimeString();
    },
    async handleRefresh() {
      await this.fetchData();
    },
    formatDate(date) {
      return format(new Date(date), "dd MMMM");
    },
    sendGreeting(user) {
      // Placeholder - can integrate with email service
      this.$bvToast.toast(`Birthday greeting queued for ${user.firstName}!`, {
        title: "Success",
        variant: "success",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.bdm-section {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.bdm-section:last-child {
  border-bottom: none;
}

.bdm-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: 500;
}

.bdm-badge {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
}

.bdm-users {
  margin-left: -12px;
  margin-right: -12px;
}

.user-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.user-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #0066cc;
}

.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
